/**
 * Centralized number utility functions
 * Provides consistent number formatting and manipulation across the Go Sit Back platform
 */
export class NumberUtils {
  /**
   * Format a number as currency
   * @param amount - Amount to format
   * @param currency - Currency code (defaults to 'USD')
   * @param locale - Locale for formatting (defaults to 'en-US')
   * @returns Formatted currency string
   */
  static formatCurrency(amount: number, currency: string = 'USD', locale: string = 'en-US'): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  /**
   * Format a number with specified decimal places
   * @param num - Number to format
   * @param decimals - Number of decimal places (defaults to 0)
   * @param locale - Locale for formatting (defaults to 'en-US')
   * @returns Formatted number string
   */
  static formatNumber(num: number, decimals: number = 0, locale: string = 'en-US'): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  }

  /**
   * Format a number as a percentage
   * @param num - Number to format (0.1 = 10%)
   * @param decimals - Number of decimal places (defaults to 1)
   * @param locale - Locale for formatting (defaults to 'en-US')
   * @returns Formatted percentage string
   */
  static formatPercentage(num: number, decimals: number = 1, locale: string = 'en-US'): string {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  }

  /**
   * Clamp a number between min and max values
   * @param value - Value to clamp
   * @param min - Minimum value
   * @param max - Maximum value
   * @returns Clamped value
   */
  static clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Round a number to specified decimal places
   * @param value - Value to round
   * @param decimals - Number of decimal places
   * @returns Rounded value
   */
  static roundToDecimals(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  /**
   * Check if a number is within a range (inclusive)
   * @param value - Value to check
   * @param min - Minimum value
   * @param max - Maximum value
   * @returns True if value is in range
   */
  static inRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max;
  }

  /**
   * Calculate percentage of a value relative to a total
   * @param value - Value
   * @param total - Total value
   * @param decimals - Number of decimal places (defaults to 2)
   * @returns Percentage as decimal (0.1 = 10%)
   */
  static percentageOf(value: number, total: number, decimals: number = 2): number {
    if (total === 0) return 0;
    return this.roundToDecimals(value / total, decimals);
  }

  /**
   * Calculate what percentage one value is of another
   * @param value - Value
   * @param total - Total value
   * @returns Percentage as whole number (10 = 10%)
   */
  static percentageOfWhole(value: number, total: number): number {
    if (total === 0) return 0;
    return this.roundToDecimals((value / total) * 100, 2);
  }

  /**
   * Parse a string to a number with fallback
   * @param str - String to parse
   * @param fallback - Fallback value if parsing fails (defaults to 0)
   * @returns Parsed number or fallback
   */
  static parseNumber(str: string, fallback: number = 0): number {
    const parsed = parseFloat(str);
    return isNaN(parsed) ? fallback : parsed;
  }

  /**
   * Convert unknown value to a number safely
   * @param value - Value to convert (can be number, string, or other)
   * @param defaultValue - Default value if conversion fails (defaults to 0)
   * @returns Converted number or default value
   */
  static toNumber(value: unknown, defaultValue: number = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return defaultValue;
  }

  /**
   * Parse a currency string to number
   * @param currencyStr - Currency string (e.g., "$1,234.56")
   * @param fallback - Fallback value if parsing fails (defaults to 0)
   * @returns Parsed number or fallback
   */
  static parseCurrency(currencyStr: string, fallback: number = 0): number {
    const cleaned = currencyStr.replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? fallback : parsed;
  }

  /**
   * Format file size in human readable format
   * @param bytes - Size in bytes
   * @param decimals - Number of decimal places (defaults to 2)
   * @returns Formatted size string
   */
  static formatFileSize(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return this.formatNumber(bytes / Math.pow(k, i), decimals) + ' ' + sizes[i];
  }

  /**
   * Generate a random number within a range
   * @param min - Minimum value (inclusive)
   * @param max - Maximum value (inclusive)
   * @param decimals - Number of decimal places (defaults to 0 for integers)
   * @returns Random number in range
   */
  static randomInRange(min: number, max: number, decimals: number = 0): number {
    const random = Math.random() * (max - min) + min;
    return decimals === 0 ? Math.floor(random) : this.roundToDecimals(random, decimals);
  }

  /**
   * Calculate compound interest
   * @param principal - Initial amount
   * @param rate - Interest rate as decimal (0.05 = 5%)
   * @param periods - Number of compounding periods
   * @param paymentsPerPeriod - Payments per period (defaults to 1)
   * @returns Final amount after compound interest
   */
  static compoundInterest(
    principal: number, 
    rate: number, 
    periods: number, 
    paymentsPerPeriod: number = 1
  ): number {
    const amount = principal * Math.pow(1 + rate / paymentsPerPeriod, paymentsPerPeriod * periods);
    return this.roundToDecimals(amount, 2);
  }

  /**
   * Formats phone number to (XXX) XXX-XXXX format
   * @param phone - Raw phone number
   * @returns Formatted phone number
   */
  static formatPhoneNumber(phone: string): string {
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }
    return phone;
  }

  /**
   * Formats postal code to XXXXX or XXXXX-XXXX format
   * @param postalCode - Raw postal code
   * @returns Formatted postal code
   */
  static formatPostalCode(postalCode: string): string {
    const digitsOnly = postalCode.replace(/\D/g, '');
    if (digitsOnly.length === 5) {
      return digitsOnly;
    } else if (digitsOnly.length === 9) {
      return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
    }
    return postalCode;
  }

  /**
   * Validates and sanitizes an array of postal codes from user input
   * Splits on multiple delimiters (comma, "and", newlines, spaces)
   * Strips non-digits and validates 5-digit format
   * Removes duplicates and returns both valid codes and rejected entries
   * 
   * @param input - Raw input string containing postal codes
   * @returns Object with valid postal codes array and rejected entries with reasons
   * 
   * @example
   * sanitizePostalCodesArray("90210, 90211 and 90212")
   * // Returns: { valid: ["90210", "90211", "90212"], rejected: [] }
   * 
   * sanitizePostalCodesArray("90210, invalid, 902")
   * // Returns: { valid: ["90210"], rejected: [{ input: "invalid", reason: "Not a number" }, { input: "902", reason: "Must be 5 digits" }] }
   */
  static sanitizePostalCodesArray(input: string): {
    valid: string[];
    rejected: Array<{ input: string; reason: string }>;
  } {
    if (!input || !input.trim()) {
      return { valid: [], rejected: [] };
    }

    const rejected: Array<{ input: string; reason: string }> = [];
    const validSet = new Set<string>();

    // Split on multiple delimiters: comma, "and", newlines, multiple spaces
    const tokens = input
      .split(/[,\n]+|\s+and\s+/i)
      .map(token => token.trim())
      .filter(Boolean);

    for (const token of tokens) {
      // Strip all non-digits
      const digitsOnly = token.replace(/\D/g, '');

      // Validation checks
      if (digitsOnly.length === 0) {
        rejected.push({ input: token, reason: 'Not a number' });
        continue;
      }

      if (digitsOnly.length !== 5) {
        rejected.push({ 
          input: token, 
          reason: `Must be 5 digits (found ${digitsOnly.length})` 
        });
        continue;
      }

      // Valid 5-digit code - add to set (automatically deduplicates)
      validSet.add(digitsOnly);
    }

    return {
      valid: Array.from(validSet).sort(),
      rejected,
    };
  }
}
